import React, { useState, useEffect, ReactNode, Children, isValidElement, useRef, startTransition } from 'react'
import classes from './styles.module.scss'
import classNames from 'classnames'
import { Swiper, SwiperSlide, SwiperClass } from 'swiper/react'
import 'swiper/css'
import { NavigationTabs } from '../NavigationTabs'
import { SwiperOptions } from 'swiper/types'
import { useWindowDimensions } from '@/hooks/utils/useWindowDimensions'

interface ISwiperTabsProps {
  children: ReactNode
  showTabs?: boolean
  tabContainerStyle?: React.CSSProperties
  navigationStyle?: React.CSSProperties
  autoWidth?: boolean
  /** Overrides the gap below the tab bar (px). Defaults to the component's 1em. */
  barSpacing?: number
}

interface ITabProps {
  label: string
  tabId: string | number // Ensure tabId is required
  isActive?: boolean
  onClick?: (tabId: string) => void
  tabStyle?: React.CSSProperties
}

interface IContentProps {
  tabId: string | number
  children: ReactNode
  tabContentStyle?: React.CSSProperties
  isActive?: boolean
}

const SwiperTabs: React.FC<ISwiperTabsProps> & {
  Tab: React.FC<ITabProps>
  Content: React.FC<IContentProps>
} = (props) => {
  const {
    children,
    showTabs = true,
    tabContainerStyle = {},
    navigationStyle = {},
    autoWidth = false,
    barSpacing
  } = props
  const [activeTab, setActiveTab] = useState<string | null>(null)
  const hasSetInitialTab = useRef(false)
  const [swiperInstance, setSwiperInstance] = useState<SwiperClass | null>(null)
  const { isMobile, isTablet } = useWindowDimensions()

  useEffect(() => {
    if (hasSetInitialTab.current) return

    const firstTab = Children.toArray(children).find((child) => {
      return isValidElement<ITabProps>(child) && child.type === SwiperTabs.Tab
    }) as React.ReactElement<ITabProps> | undefined

    if (firstTab) {
      startTransition(() => setActiveTab(firstTab.props.tabId.toString()))
      hasSetInitialTab.current = true
    }
  }, [children])

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId)
  }

  const getSwiperConfig: () => SwiperOptions = () => {
    if (autoWidth) {
      return {
        allowTouchMove: true,
        autoplay: false,
        slidesPerView: 'auto',
        spaceBetween: 8,
        watchOverflow: true,
      }
    }

    return {
      allowTouchMove: true,
      autoplay: false,
      slidesPerView: 3,
      spaceBetween: 0,
      breakpoints: {
        300: {
          slidesPerView: 2,
        },
        640: {
          slidesPerView: 2,
        },
        768: {
          slidesPerView: 3,
        },
        1024: {
          slidesPerView: 3,
        },
      }
    }
  }

  const SwiperConfig = getSwiperConfig()

  return (
    <div className={classes.SwiperTabs}>
      <div style={{ ...tabContainerStyle }}>
        {showTabs &&
          <div
            className={classNames({
              [classes.tabs]: true,
              [classes.autoWidthTabs]: autoWidth,
              [classes.fullWidth]: (isMobile || isTablet) || (!swiperInstance?.allowSlidePrev && !swiperInstance?.allowSlideNext),
            })}
            style={barSpacing !== undefined ? { marginBottom: barSpacing } : undefined}
          >
            <Swiper
              navigation={false}
              onSwiper={(swiper) => setSwiperInstance(swiper)}
              {...SwiperConfig}
            >
              {Children.map(children, (child) => {
                if (isValidElement<ITabProps>(child) && child.type === SwiperTabs.Tab) {
                  return (
                    <SwiperSlide>
                      {React.cloneElement(child, {
                        isActive: child.props.tabId === activeTab,
                        onClick: handleTabClick,
                      })}
                    </SwiperSlide>
                  )
                }
                return null
              })}
            </Swiper>
            {swiperInstance && (swiperInstance?.allowSlidePrev || swiperInstance?.allowSlideNext) && (!isMobile && !isTablet) &&
              <NavigationTabs
                swiper={swiperInstance}
                navigationStyle={navigationStyle}
              />
            }
          </div>
        }
      </div>
      <div className={classes.content}>
        {Children.map(children, (child) => {
          if (isValidElement<IContentProps>(child) && child.type === SwiperTabs.Content) {
            const isActive = child.props.tabId === activeTab
            return React.cloneElement(child, { isActive })
          }
          return null
        })}
      </div>
    </div >
  )
}

SwiperTabs.Tab = Object.assign(({ label, tabId, isActive, onClick, tabStyle }: ITabProps) => {
  return (
    <button
      className={`${classes.tab} ${isActive ? classes.active : ''}`}
      onClick={() => onClick?.(tabId.toString())}
      style={{ ...tabStyle }}
    >
      {label}
    </button>
  )
}, { displayName: 'SwiperTabs.Tab' })

SwiperTabs.Content = Object.assign(({ children, isActive, tabContentStyle }: IContentProps & { isActive?: boolean }) => {
  return (
    <div
      className={`${classes.tabContent} ${isActive ? classes.activeContent : ''}`}
      style={{ ...tabContentStyle }}
    >
      {children}
    </div>
  )
}, { displayName: 'SwiperTabs.Content' })


export { SwiperTabs }